import { firebasePrefix, isSelfReconnecting, monitorConnection, onValueAt, reconnect } from './firebase'
import { isDisplayMessage, normalizeDisplayMessage, type DisplayMessage, type Place } from '../types/display'

export type ConnectionStatus = 'online' | 'offline'

/** `moment` ends the current payment moment and leaves the cart under it,
 *  `all` takes the screen back to welcome. */
export type ClearScope = 'moment' | 'all'

/** What a source tells the screen. Only `message` is required: the demo and
 *  e2e sources have no link and no branding of their own. */
export interface SourceListener {
  /** `stored` marks what was waiting when the display subscribed, as opposed
   *  to a live push. */
  message(message: DisplayMessage, stored?: boolean): void
  /** The link to the till dropping and coming back. */
  status?(status: ConnectionStatus): void
  /** For sources that can take a screen away. Firebase never does: a cleared
   *  path keeps the screen. */
  clear?(scope: ClearScope): void
  /** The shop's title, logo and so on. `null` means not known, or no longer
   *  allowed to show. */
  branding?(place: Place | null): void
}

export interface MessageSource {
  subscribe(listener: SourceListener): () => void
  /** Branding from this source may only show once its grant is confirmed, so
   *  nothing saved from an earlier visit is painted first. */
  readonly brandingNeedsGrant?: boolean
}

export function cfdPath(placeId: string, userId: string): string {
  return `${firebasePrefix}/cfd/${placeId}/${userId}`
}

/** The cashier wraps every push as `{ data: … }`. */
export function unwrap(value: unknown): DisplayMessage | null {
  if (isDisplayMessage(value)) return normalizeDisplayMessage(value)
  if (typeof value === 'object' && value !== null && 'data' in value) {
    const inner = (value as { data: unknown }).data
    if (isDisplayMessage(inner)) return normalizeDisplayMessage(inner)
  }
  return null
}

export function createRtdbSource(placeId: string, userId: string): MessageSource {
  return {
    subscribe(listener) {
      const path = cfdPath(placeId, userId)
      let active = true

      let reconnectTimer: ReturnType<typeof setTimeout> | null = null

      // One value listener covers the stored message at boot and every push
      // after, including the push that recreates the path once the till has
      // cleared it. A cleared path (null) leaves the screen as it was.
      // The first value is the stored one even when it is null, so a push
      // after a cleared path is live, not stored.
      let first = true
      const detachValue = onValueAt(path, (value) => {
        const stored = first
        first = false
        const message = unwrap(value)
        if (active && message) listener.message(message, stored)
      })

      const detachConnection = monitorConnection(
        () => {
          // The disconnect our own reconnect() causes must not schedule another
          // one, or the socket bounces every few seconds forever.
          if (isSelfReconnecting()) return

          if (active) listener.status?.('offline')
          console.warn('[Firebase] Disconnected — reconnecting in 3s')
          if (reconnectTimer) clearTimeout(reconnectTimer)
          reconnectTimer = setTimeout(() => reconnect(), 3000)
        },
        () => {
          if (active) listener.status?.('online')
          console.info('[Firebase] Reconnected')
        },
      )

      return () => {
        active = false
        if (reconnectTimer) clearTimeout(reconnectTimer)
        detachValue()
        detachConnection()
      }
    },
  }
}
